import { useEffect, useSyncExternalStore } from 'react';
import { errorText } from '../ui/toast.tsx';
import { getAuthStore } from '../auth/authStore.ts';
import { getNotificationService } from './service.ts';
import { WebPushTransport, type ServerNotificationStatus } from './webPush.ts';

/**
 * Serverns notisstatus (Web Push), delad mellan Idag och Påminnelsekoll.
 *  - Idag nöjer sig med en kontroll som är högst SUMMARY_TTL gammal; den sparas för fliken och profilen
 *    i sessionStorage som en MINIMAL ögonblicksbild (inga titlar eller annan kalendertext), så att en
 *    omladdning inte ger ett nytt API-anrop varje gång.
 *  - Påminnelsekoll hämtar fullständig status när den visas (om den är äldre än DETAIL_TTL eller bara
 *    minimal) och när användaren väljer "Kontrollera igen".
 * Ett profilbyte laddar om sidan; nyckeln innehåller profilens id, så ett konto ser aldrig ett annat
 * kontos status.
 */
export const DETAIL_TTL_MS = 60_000;
export const SUMMARY_TTL_MS = 5 * 60_000;

interface Snapshot {
  status: ServerNotificationStatus | null;
  error: string | null;
  checkedAt: string | null;
  /** true = från fliklagringen (utan planerade titlar, fel och andra enheter). */
  partial: boolean;
}

let snap: Snapshot = { status: null, error: null, checkedAt: null, partial: false };
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function key(): string | null {
  try {
    return 'autoday.serverStatus.v1:' + getAuthStore().profile.id;
  } catch {
    return null;
  }
}

function ss(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function loadPersisted() {
  if (loaded) return;
  loaded = true;
  const k = key();
  const raw = k ? ss()?.getItem(k) : null;
  if (!raw) return;
  try {
    const m = JSON.parse(raw) as { at: string; pushConfigured: boolean; workerStale: boolean; deviceSubscribed?: boolean; lastAcceptedAt: string | null; lastDisplayedTest?: ServerNotificationStatus['lastDisplayedTest']; serverHorizonHours?: number; recentErrorCount: number };
    snap = {
      status: {
        workerHeartbeatAt: null,
        workerStale: !!m.workerStale,
        lastAcceptedAt: m.lastAcceptedAt ?? null,
        nextPlanned: [],
        recentErrors: Array.from({ length: Math.min(5, m.recentErrorCount ?? 0) }, () => ({ at: m.at, message: '' })),
        pushConfigured: !!m.pushConfigured,
        ...(m.deviceSubscribed !== undefined ? { deviceSubscribed: m.deviceSubscribed } : {}),
        ...(m.serverHorizonHours !== undefined ? { serverHorizonHours: m.serverHorizonHours } : {}),
        lastDisplayedTest: m.lastDisplayedTest ?? null,
      },
      error: null,
      checkedAt: m.at,
      partial: true,
    };
  } catch {
    /* ogiltig post: hämtas på nytt */
  }
}

function persist(s: ServerNotificationStatus, at: string) {
  const k = key();
  if (!k) return;
  try {
    ss()?.setItem(
      k,
      JSON.stringify({ at, pushConfigured: s.pushConfigured, workerStale: s.workerStale, deviceSubscribed: s.deviceSubscribed, lastAcceptedAt: s.lastAcceptedAt, lastDisplayedTest: s.lastDisplayedTest ?? null, serverHorizonHours: s.serverHorizonHours, recentErrorCount: s.recentErrors.length }),
    );
  } catch {
    /* lagring blockerad: bara minnet */
  }
}

export function refreshServerStatus(opts: { force?: boolean; maxAgeMs?: number; full?: boolean } = {}): Promise<void> {
  const svc = getNotificationService();
  if (!(svc.transport instanceof WebPushTransport)) return Promise.resolve();
  loadPersisted();
  if (inflight) return inflight;
  const age = snap.checkedAt ? Date.now() - Date.parse(snap.checkedAt) : Infinity;
  if (!opts.force && age < (opts.maxAgeMs ?? DETAIL_TTL_MS) && !(opts.full && snap.partial)) return Promise.resolve();
  const transport = svc.transport;
  inflight = (async () => {
    const at = new Date().toISOString();
    try {
      const status = await transport.serverStatus();
      snap = { status, error: null, checkedAt: at, partial: false };
      persist(status, at);
    } catch (e) {
      snap = { ...snap, error: errorText(e), checkedAt: at };
    } finally {
      inflight = null;
      for (const l of listeners) l();
    }
  })();
  return inflight;
}

/**
 * detail = Påminnelsekoll (fullständig, högst 1 min gammal); annars sammanfattning (högst 5 min).
 * enabled = false: hämta inget (sammanfattningen behöver inte servern när de lokala kontrollerna redan
 * avgör bedömningen, t.ex. utan behörighet eller prenumeration).
 */
export function useServerStatus(trigger: unknown, detail = false, enabled = true): Snapshot {
  loadPersisted();
  const s = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => snap,
    () => snap,
  );
  useEffect(() => {
    if (!enabled) return;
    void refreshServerStatus(detail ? { maxAgeMs: DETAIL_TTL_MS, full: true } : { maxAgeMs: SUMMARY_TTL_MS });
  }, [trigger, detail, enabled]);
  return s;
}
